import React from "react";
import * as m from "../../lib/model";
import { Card, Tile } from "../shared";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from "recharts";
import { fmtBig, entryMult, filled, defaultLevers, DECK_GREEN } from "./deck";
import { Banner } from "./Banner";

// Typed-in economics on the narrative block win over the portfolio record; blanks fall through.
export const mergeOwn = (own, block = {}) => {
  const typed = Object.entries(block.own || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined && !(typeof v === "number" && v === 0));
  if (!own && !typed.length) return null;
  return { ...(own || {}), ...Object.fromEntries(typed) };
};

const range = (arr, f) => (arr.length ? (Math.min(...arr) === Math.max(...arr) ? f(arr[0]) : `${f(Math.min(...arr))} – ${f(Math.max(...arr))}`) : null);
export const rollupCos = (M) => ((M.rollup && M.rollup.companies) || []).filter((c) => c.name && +c.year > 0);

export function ProfileTable({ rows, title, testid }) {
  return (
    <Card title={title} testid={testid}>
      <div className="ir-profile">{rows.map((r, i) => <React.Fragment key={i}><span>{r.label}</span><div>{r.value}</div></React.Fragment>)}</div>
    </Card>
  );
}

export function WhyGrid({ items, testid }) {
  return (
    <div className="ir-why-list" data-testid={testid}>
      {items.map((w, i) => <div className="ir-why-item" key={i} data-testid={`${testid}-${i}`}><b>{w.title}</b><span>{w.text}</span></div>)}
    </div>
  );
}

// ---------- Platform economics (from the Portfolio record) ----------
export function PlatformFacts({ M, own = {} }) {
  const R = m.recapCfg(M);
  const ebitda = +own.ebitda || M.sde || 0;
  const mult = +own.multiplePaid || +R.recapMult || 0;
  const paid = ebitda && mult ? ebitda * mult : m.isRecap(M) ? M.askPrice : m.nac(M);
  const pctOwned = own.ownershipPct ?? (M.rollPct > 0 ? 100 - M.rollPct : 100);
  return (
    <div className="grid g4" data-testid="ir-platform-facts">
      <Tile label="Platform EBITDA" value={m.fmt$(ebitda)} sub={own.revenue ? `${m.fmt$(own.revenue)} revenue` : ""} tone="go" testid="ir-pf-ebitda" />
      <Tile label="Acquired for" value={m.fmt$(paid)} sub={own.acquiredYear ? `acquired ${own.acquiredYear}` : ""} testid="ir-pf-paid" />
      <Tile label="Multiple paid" value={mult > 0 ? m.fmtX(mult) : "—"} sub="× EBITDA at entry" testid="ir-pf-mult" />
      <Tile label="Sponsor ownership" value={`${Math.round(pctOwned)}%`} sub={+own.debt > 0 ? `${m.fmt$(own.debt)} platform debt` : "owned by the sponsor"} testid="ir-pf-owned" />
    </div>
  );
}

// ---------- The platform / the business ----------
export function PlatformBlock({ M, title, block = {}, platformMode, own }) {
  const cat = (M.industry || M.businessType || "operating").toLowerCase();
  const R = m.recapCfg(M), recap = m.isRecap(M);
  const mult = +(own?.multiplePaid) || (recap ? +R.recapMult || 0 : entryMult(M));
  const paid = own ? (+own.ebitda || 0) * (+own.multiplePaid || 0) : recap ? M.askPrice : m.nac(M);
  const ownPct = own?.ownershipPct ?? (M.rollPct > 0 ? 100 - M.rollPct : 100);
  const platDebt = own ? +own.debt || 0 : recap ? +R.existingDebt || 0 : 0;
  const shortTitle = String(title || "").replace(/\s*[—–-]\s*(recapitalization|recapitalisation|recap)\s*$/i, "");
  const headline = block.headline || (platformMode
    ? `${shortTitle} — an established ${cat} platform${M.address ? ` for a ${M.address.split(",").pop().trim()} roll-up` : ""}`
    : `${shortTitle} — an established ${cat} business${M.address ? ` in ${M.address}` : ""}`);
  const economics = [
    ["Platform EBITDA", (own?.ebitda || M.sde) ? m.fmt$(own?.ebitda || M.sde) : null],
    ["Acquired for", paid > 0 ? m.fmt$(paid) : null],
    ["Multiple paid", mult > 0 ? `${m.fmtX(mult)} EBITDA` : null],
    ["Sponsor ownership", ownPct ? `${Math.round(ownPct)}% owned` : null],
    ["Platform debt", platDebt > 0 ? m.fmt$(platDebt) : null],
    ["Acquired", own?.acquiredYear || null],
  ];
  const profile = filled(block.profile, "value").length ? filled(block.profile, "value") : [
    ["Location", M.address], ["TTM revenue", M.ttmRevenue ? m.fmt$(M.ttmRevenue) : null],
    ...economics,
    ["Gross margin", m.grossMargin(M) ? m.fmtPct(m.grossMargin(M)) : null],
    ["Employees", M.employeeCount], ["Established", M.yearEstablished],
  ].filter((r) => r[1]).map(([label, value]) => ({ label, value }));
  const facts = profile.filter((r) => !/^(category|platform debt|deal type|acquired for|multiple paid)$/i.test(String(r.label).trim()));
  const dscr = m.dscrYr1(M);
  const note = parsePlatformNote(own?.notes);
  const noteThesis = note.find((n) => /thesis|platform note/i.test(n.title));
  const noteRest = note.filter((n) => n !== noteThesis);
  const why = filled(block.why, "title").length ? filled(block.why, "title") : [
    { title: "Cash coverage", text: `Year-one debt service covered ${m.fmtX(dscr)} on ${m.fmt$(M.sde)} of adjusted earnings — distributions do not depend on growth.` },
    { title: "Operating muscle", text: `${M.employeeCount ? `${M.employeeCount} employees and ` : ""}an established ${cat} operation with the systems to ${platformMode ? "absorb bolt-ons" : "run without the owner"}.` },
    { title: "Geographic fit", text: `${M.address || "The home territory"} is the natural hub for ${platformMode ? "add-ons in the corridor" : "customers and recruiting"}.` },
    { title: "Owner alignment", text: M.rollPct > 0 ? `Seller rolls ${M.rollPct}% (${m.fmt$(m.rolloverAmt(M))}) alongside investors — continuity for customers and staff.` : "Sponsor equity commitment with management continuity through the transition." },
  ];
  return (
    <div className="ir-gap" data-testid="ir-platform">
      <Banner photos={[...(block.photos || []), block.photo]} alt={`${shortTitle} platform`} testid="ir-platform-photo" />
      <div className="ir-deck-head">
        <span className="ir-eyebrow">{platformMode ? "The platform" : "The business"}</span>
        <h2 className="ir-deck-h sm" data-testid="ir-platform-headline">{headline}</h2>
        {noteThesis && <p className="ir-para wide" data-testid="ir-platform-thesis">{noteThesis.text}</p>}
      </div>
      <div className="ir-fact-grid" data-testid="ir-platform-profile">
        {facts.map((r, i) => (
          <div className="ir-fact" key={i} data-testid={`ir-platform-fact-${i}`}>
            <span>{r.label}</span><b>{r.value}</b>
          </div>
        ))}
      </div>
      <Card title={`Why ${shortTitle} is the right ${platformMode ? "platform" : "acquisition"}`} className="ir-gap" testid="ir-platform-why-card">
        <WhyGrid items={why} testid="ir-platform-why" />
      </Card>
      {noteRest.length > 0 && (
        <div className="ir-why-grid lg-grid" style={{ marginTop: 14 }} data-testid="ir-platform-playbook">
          {noteRest.map((n, i) => <div className="ir-why-card lg" key={i} data-testid={`ir-platform-note-${i}`}><b>{n.title}</b><span>{n.text}</span></div>)}
        </div>
      )}
    </div>
  );
}

// "Thesis: … Add-on criteria: … Integration playbook: …" platform notes → titled sections.
const NOTE_KEYS = ["Thesis:", "Add-on criteria:", "Integration playbook:"];
export const parsePlatformNote = (t) => {
  const s = String(t || "").trim();
  if (!s) return [];
  const hits = NOTE_KEYS.map((k) => ({ k, i: s.indexOf(k) })).filter((x) => x.i >= 0).sort((a, b) => a.i - b.i);
  if (!hits.length) return [{ title: "Platform note", text: s }];
  return hits.map((x, n) => ({ title: x.k.replace(":", ""), text: s.slice(x.i + x.k.length, n + 1 < hits.length ? hits[n + 1].i : undefined).trim() })).filter((x) => x.text);
};

// ---------- Acquisition strategy (recap / roll-up only) ----------
export function AcqStrategyBlock({ M, thesis = {}, block = {} }) {
  const cos = rollupCos(M);
  const mults = cos.map((c) => +c.multiple).filter(Boolean), eb = cos.map((c) => +c.ebitda).filter(Boolean);
  const warChest = m.isRecap(M) ? m.recapModel(M).warChestTotal : 0;
  const deployRows = filled(block.deploy, "label").filter((d) => +d.amount > 0);
  const deploy = deployRows.length ? deployRows : cos.map((c, i) => ({ label: c.name || `Add-on ${i + 1}`, amount: (+c.ebitda || 0) * (+c.multiple || 0) }));
  const spent = deploy.reduce((a, d) => a + +d.amount, 0);
  if (warChest > spent && !deployRows.length) deploy.push({ label: "Reserve", amount: warChest - spent });
  const total = deploy.reduce((a, d) => a + +d.amount, 0);
  const platformEbitda = (M.sde || 0) + eb.reduce((a, b) => a + b, 0);
  const headline = block.headline || (cos.length
    ? `Buy ${cos.length} lower-middle-market add-on${cos.length === 1 ? "" : "s"}${mults.length ? ` at ${range(mults, (x) => x.toFixed(1))}×` : ""} — build a ${fmtBig(platformEbitda)} EBITDA platform`
    : `Deploy ${fmtBig(warChest || m.lpEquityBase(M))} into add-ons — build a ${fmtBig(platformEbitda)} EBITDA platform`);
  const profile = filled(block.profile, "value").length ? filled(block.profile, "value") : [
    ["EBITDA", eb.length ? range(eb, m.fmt$) : "$300,000 – $1,500,000"], ["Geography", M.address ? `${M.address.split(",").pop().trim()} priority — same corridor as the platform` : "Contiguous to the platform"],
    ["Entry multiple", `${mults.length ? range(mults, (x) => x.toFixed(1)) : `${Math.max(2.5, entryMult(M) - 0.5).toFixed(1)} – ${(entryMult(M) + 1).toFixed(1)}`}× LTM EBITDA (SDE-adjusted)`],
    ["Service overlap", "Same trade / customer set — shared crews, parts and back office"], ["Owner situation", "Retiring / no succession preferred"], ["Funding", warChest > 0 ? `${fmtBig(warChest)} war chest, no acquisition leverage at close` : "Free cash flow plus retained equity"],
  ].map(([label, value]) => ({ label, value }));
  const levers = filled(thesis.levers, "title").length ? filled(thesis.levers, "title") : defaultLevers(M);
  return (
    <div className="ir-gap" data-testid="ir-acq">
      <div className="ir-deck-head">
        <span className="ir-eyebrow">Acquisition strategy</span>
      </div>
      <div className="ir-2col eq">
        <ProfileTable title="Target profile" rows={profile} testid="ir-acq-profile" />
        <Card title="Capital deployment" testid="ir-acq-deploy">
          {deploy.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={deploy.map((d) => ({ name: d.label, v: Math.round(+d.amount || 0) }))} margin={{ top: 22, right: 8, left: 6, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: "var(--text3)" }} axisLine={false} tickLine={false} interval={0} />
                <YAxis tick={{ fontSize: 10.5, fill: "var(--text3)" }} axisLine={false} tickLine={false} width={78} tickFormatter={(v) => m.fmt$(v)} />
                <Bar dataKey="v" fill={DECK_GREEN} radius={[2, 2, 0, 0]}><LabelList dataKey="v" position="top" formatter={(v) => m.fmt$(v)} style={{ fontSize: 10.5, fill: "var(--text)", fontFamily: "inherit" }} /></Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="bullet">Add roll-up companies on Assumptions, or enter a deployment plan in the room editor.</div>}
          <div className="ir-foot-note">Illustrative deployment of {fmtBig(total)} across {deploy.filter((d) => !/reserve/i.test(d.label)).length} add-ons{deploy.some((d) => /reserve/i.test(d.label)) ? " + a working-capital / integration reserve" : ""}. Actual mix will vary with target LTM EBITDA and multiples negotiated.</div>
        </Card>
      </div>
      <div className="ir-levers-band ir-gap" data-testid="ir-acq-levers">
        <b>Value creation levers</b>
        <div className="ir-levers-row">
          {levers.slice(0, 4).map((l, i) => <div key={i}><span className="mono">{i + 1}</span><b>{l.title}</b><p>{l.text}</p></div>)}
        </div>
      </div>
    </div>
  );
}
